import { formatValue } from "../../utils/helpers";
import metricMetadataIcon from '../../assets/general/metric-metadata-icon-credc.svg';
import { useState } from "react";
import MetricMetadataModal from "../modals/MetricMetadataModal";
import { DisaggregatedMetric, DistributionData, GeoDetails, GeoTag, MetricMetadata, RawIndicatorData } from "../../services/types";
import { fetchDistributionData, fetchIndicatorBinCount, fetchMetricMetadata } from "../../utils/dashboardUtils";
import DefaultIcon from '../../assets/general/default-metric.svg?react';
import { useDashboardColor } from "../../context/DashboardColorContext";

interface MetricCardProps {
    metric: DisaggregatedMetric;
    width: string;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    svg?: React.FC<React.SVGProps<SVGSVGElement>>;
    showMetadataIcon?: boolean;
};

const MetricCard = ({ metric, disaggData, geoDetails, geoTag, selectionActive, svg: SvgIcon, showMetadataIcon = true }: MetricCardProps) => {
    const color = useDashboardColor();
    const [showMetadata, setShowMetadata] = useState(false);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Destructure metric properties
    const { id, name, versions } = metric;
    const { value, unit, unit_descriptor, percent_calc } = versions[0];

    const val = unit === "ratio" ? `${percent_calc?.n}/${percent_calc?.d}` : value;

    const IconComponent = SvgIcon || DefaultIcon;

    const handleClick = async () => {
        setShowMetadata((prev) => !prev);

        if (metadata && distributionData && countyDistributionData) {
            // Skip fetching if all data is already present
            return;
        }

        setLoading(true);

        const fetchMetadata = metadata ? Promise.resolve({ data: metadata }) : fetchMetricMetadata(id);
        const fetchDistData = distributionData ? Promise.resolve(distributionData) : fetchIndicatorBinCount(id, geoDetails, geoTag);
        const fetchCountyDist = countyDistributionData ? Promise.resolve(countyDistributionData) : fetchDistributionData(id, [], null);

        try {
            const [metadataResponse, distributionDataResponse, countyDistributionDataResponse] = await Promise.all([
                fetchMetadata,
                fetchDistData,
                fetchCountyDist,
            ]);

            if (!metadata) setMetadata(metadataResponse?.data || null);
            if (!distributionData) setDistributionData(distributionDataResponse || null);
            if (!countyDistributionData) setCountyDistributionData(countyDistributionDataResponse || null);

        } catch (error) {
            const errorMessage = (error as Error).message || 'An unexpected error occurred.';
            console.error('Error fetching data:', error);
            setError(new Error(`Failed to load data: ${errorMessage}`)); // Pass detailed error message
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mx-2 my-2">
            <div className="flex flex-col items-start w-full">
                <div className="flex items-center justify-start">
                    <IconComponent
                        className="w-10 h-10"
                        style={{ color: color }}
                        aria-label={`${name} icon`}
                        role="img"
                    />
                    
                    <h2 className="text-3xl font-barlow-condensed font-bold ml-2" style={{ color: color }}>{formatValue(val, unit, id)}</h2>
                </div>
                <div className="flex my-1">
                    <p className="text-port-text text-xs font-bold">{name}</p>

                    {showMetadataIcon && 
                        <div className="shrink-0 -mt-[3px] ml-2 group relative">
                            <button onClick={handleClick} title="View more information">
                                <img src={metricMetadataIcon} alt="Metric metadata icon"/>
                            </button>
                        </div>
                    }
                </div>
                {unit_descriptor && <p className={`text-port-text text-xs -mt-1`}>{unit_descriptor}</p>}
            </div>
            <MetricMetadataModal
                isOpen={showMetadata}
                onClose={handleClick}
                id={id}
                metadata={metadata}
                stateData={null}
                nationalData={null}
                disaggData={disaggData}
                distributionData={distributionData}
                countyDistributionData={countyDistributionData}
                selectionActive={selectionActive}
                geoTag={geoTag}
                svg={SvgIcon}
                loading={loading}
                error={error}
            />
        </div>
    );
};


export default MetricCard;
